// ====================================
// TEMA UNIFICATO LANDING PAGES
// ====================================
// Tema basato sullo stile della PricingPage
// Da utilizzare in tutte le pagine landing per consistenza visiva

import { createTheme } from '@mui/material/styles';

// ==================== PALETTE COLORI ====================
export const landingColors = {
  // Colori Primari
  primary: '#2196F3',           // Blu principale
  primaryLight: '#42A5F5',      // Blu chiaro
  primaryDark: '#1976D2',       // Blu scuro
  primaryLighter: '#90CAF9',    // Blu molto chiaro
  
  // Colori Secondari
  secondary: '#FF6B35',         // Arancione/Rosso accento
  secondaryLight: '#FF8555',    // Arancione chiaro
  secondaryDark: '#E55A25',     // Arancione scuro
  
  // Colori Successo/Errore/Warning
  success: '#4CAF50',           // Verde successo
  error: '#F44336',             // Rosso errore
  warning: '#FF9800',           // Arancione warning
  info: '#2196F3',              // Blu info
  
  // Gradiente di Background
  gradientStart: '#2196F3',     // Inizio gradiente (blu)
  gradientEnd: '#42A5F5',       // Fine gradiente (blu chiaro)
  
  // Colori di Testo
  textPrimary: '#1a1a1a',       // Testo principale scuro
  textSecondary: '#666',        // Testo secondario grigio
  textLight: '#ffffff',         // Testo bianco
  textMuted: 'rgba(0,0,0,0.6)', // Testo attenuato
  
  // Colori di Background
  bgLight: '#f5f5f5',           // Background chiaro
  bgWhite: '#ffffff',           // Background bianco
  bgDark: '#1a1a2e',            // Background scuro (footer)
  bgGlass: 'rgba(255,255,255,0.98)', // Effetto vetro
  bgOverlay: 'rgba(255,255,255,0.15)', // Overlay trasparente
  
  // Bordi e Divider
  border: 'rgba(255,255,255,0.3)',
  borderDark: 'rgba(0,0,0,0.12)',
  divider: 'rgba(255,255,255,0.2)',
};

// ==================== GRADIENTI ====================
export const landingGradients = {
  primary: `linear-gradient(135deg, ${landingColors.gradientStart} 0%, ${landingColors.gradientEnd} 100%)`,
  primaryReverse: `linear-gradient(135deg, ${landingColors.gradientEnd} 0%, ${landingColors.gradientStart} 100%)`,
  primaryVertical: `linear-gradient(180deg, ${landingColors.gradientStart} 0%, ${landingColors.gradientEnd} 100%)`,
  overlay: 'linear-gradient(135deg, rgba(33,150,243,0.1) 0%, rgba(66,165,245,0.05) 100%)',
  card: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
};

// ==================== OMBRE (SHADOWS) ====================
export const landingShadows = {
  card: '0 8px 32px rgba(33,150,243,0.15)',
  cardHover: '0 12px 48px rgba(33,150,243,0.25)',
  button: '0 4px 14px rgba(33,150,243,0.3)',
  buttonHover: '0 6px 20px rgba(33,150,243,0.4)',
  soft: '0 2px 12px rgba(0,0,0,0.08)',
  medium: '0 4px 20px rgba(0,0,0,0.1)',
  strong: '0 8px 32px rgba(0,0,0,0.15)',
  text: '2px 2px 4px rgba(0,0,0,0.3)',
  textLight: '1px 1px 2px rgba(0,0,0,0.3)',
};

// ==================== SPACING ====================
export const landingSpacing = {
  xs: 8,
  sm: 16,
  md: 24,
  lg: 32,
  xl: 48,
  xxl: 64,
  section: 80,
};

// ==================== TYPOGRAPHY ====================
export const landingTypography = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  
  // Hero Section
  heroTitle: {
    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.75rem' },
    fontWeight: 800,
    lineHeight: 1.2,
    color: landingColors.textLight,
    textShadow: landingShadows.text,
  },
  heroSubtitle: {
    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
    fontWeight: 400,
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.9)',
    textShadow: landingShadows.textLight,
  },
  
  // Section Headers
  sectionTitle: {
    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
    fontWeight: 800,
    lineHeight: 1.3,
    color: landingColors.textLight,
    textShadow: landingShadows.text,
  },
  sectionSubtitle: {
    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
    fontWeight: 400,
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.9)',
  },
  
  // Card Typography
  cardTitle: {
    fontSize: { xs: '1.5rem', sm: '1.75rem' },
    fontWeight: 700,
    lineHeight: 1.3,
  },
  cardDescription: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.7,
    color: landingColors.textSecondary,
  },
  
  // Body Text
  body: {
    fontSize: '1rem',
    lineHeight: 1.7,
    color: landingColors.textPrimary,
  },
  bodyLarge: {
    fontSize: '1.1rem',
    lineHeight: 1.8,
    color: landingColors.textPrimary,
  },
};

// ==================== COMPONENTI COMUNI ====================
export const landingComponents = {
  // Chip Label Style
  chip: {
    bgcolor: landingColors.bgOverlay,
    color: landingColors.textLight,
    fontWeight: 700,
    px: 2,
    py: 0.5,
    border: `1px solid ${landingColors.border}`,
    fontSize: '0.875rem',
    letterSpacing: '0.5px',
  },
  
  // Card Style
  card: {
    bgcolor: landingColors.bgWhite,
    borderRadius: 3,
    boxShadow: landingShadows.card,
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: landingShadows.cardHover,
      transform: 'translateY(-8px)',
    },
  },
  
  // Glass Card (semi-transparent)
  glassCard: {
    bgcolor: landingColors.bgOverlay,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${landingColors.border}`,
    borderRadius: 3,
    boxShadow: landingShadows.soft,
  },
  
  // Primary Button
  primaryButton: {
    bgcolor: landingColors.textLight,
    color: landingColors.primary,
    fontWeight: 700,
    fontSize: '1.1rem',
    px: 4,
    py: 1.5,
    borderRadius: 2,
    textTransform: 'none',
    boxShadow: landingShadows.button,
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.95)',
      boxShadow: landingShadows.buttonHover,
      transform: 'translateY(-2px)',
    },
  },
  
  // Secondary Button
  secondaryButton: {
    bgcolor: 'transparent',
    color: landingColors.textLight,
    fontWeight: 700,
    fontSize: '1.1rem',
    px: 4,
    py: 1.5,
    borderRadius: 2,
    border: `2px solid ${landingColors.textLight}`,
    textTransform: 'none',
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.1)',
      borderWidth: 2,
      transform: 'translateY(-2px)',
    },
  },
  
  // Container Background
  pageContainer: {
    minHeight: '100vh',
    background: landingGradients.primary,
    position: 'relative',
  },
  
  // Section Container
  section: {
    py: { xs: 6, md: 8 },
    px: { xs: 2, sm: 3 },
  },
};

// ==================== ANIMAZIONI ====================
export const landingAnimations = {
  fadeIn: {
    '@keyframes fadeIn': {
      from: { opacity: 0, transform: 'translateY(20px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    animation: 'fadeIn 0.6s ease-out',
  },
  
  slideIn: {
    '@keyframes slideIn': {
      from: { opacity: 0, transform: 'translateX(-30px)' },
      to: { opacity: 1, transform: 'translateX(0)' },
    },
    animation: 'slideIn 0.6s ease-out',
  },
  
  scaleIn: {
    '@keyframes scaleIn': {
      from: { opacity: 0, transform: 'scale(0.9)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animation: 'scaleIn 0.5s ease-out',
  },
  
  float: {
    '@keyframes float': {
      '0%, 100%': { transform: 'translateY(0px)' },
      '50%': { transform: 'translateY(-10px)' },
    },
    animation: 'float 3s ease-in-out infinite',
  },
};

// ==================== BREAKPOINTS ====================
export const landingBreakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// ==================== TEMA MATERIAL-UI ====================
export const landingMuiTheme = createTheme({
  palette: {
    primary: {
      main: landingColors.primary,
      light: landingColors.primaryLight,
      dark: landingColors.primaryDark,
    },
    secondary: {
      main: landingColors.secondary,
      light: landingColors.secondaryLight,
      dark: landingColors.secondaryDark,
    },
    success: {
      main: landingColors.success,
    },
    error: {
      main: landingColors.error,
    },
    warning: {
      main: landingColors.warning,
    },
    info: {
      main: landingColors.info,
    },
    text: {
      primary: landingColors.textPrimary,
      secondary: landingColors.textSecondary,
    },
    background: {
      default: landingColors.bgLight,
      paper: landingColors.bgWhite,
    },
  },
  typography: {
    fontFamily: landingTypography.fontFamily,
  },
  breakpoints: {
    values: landingBreakpoints,
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    landingShadows.soft,
    landingShadows.medium,
    landingShadows.strong,
    landingShadows.card,
    landingShadows.cardHover,
    '0 8px 16px rgba(0,0,0,0.15)',
    '0 12px 24px rgba(0,0,0,0.15)',
    '0 16px 32px rgba(0,0,0,0.15)',
    '0 20px 40px rgba(0,0,0,0.15)',
    '0 24px 48px rgba(0,0,0,0.15)',
    ...Array(14).fill('0 24px 48px rgba(0,0,0,0.15)'),
  ],
});

// ==================== EXPORT DEFAULT ====================
export default {
  colors: landingColors,
  gradients: landingGradients,
  shadows: landingShadows,
  spacing: landingSpacing,
  typography: landingTypography,
  components: landingComponents,
  animations: landingAnimations,
  breakpoints: landingBreakpoints,
  muiTheme: landingMuiTheme,
};
